/* =====================================================================================
   Failures the person can see.

   THE PATTERN THIS REPLACES.

   Roughly twenty places did this:

       fetchDeadlines().then(setDeadlines).catch(() => setDeadlines([]));

   which renders a failed query and a genuinely empty table identically. The Atlas showed
   dropdowns with nothing in them, Docket showed no deadlines, Quality showed no documents,
   and in each case the screen looked like an empty database rather than a broken request.
   That is the worst possible failure mode for a business system: it is indistinguishable
   from a true statement, so nobody investigates, and the person plans around data that is
   not there.

   It also made the application undiagnosable from the outside. When the report was "Crew is
   empty" there was no way to tell a permissions failure from a missing seed without opening
   the console.

   WHAT REPLACES IT.

   The same call sites now say what they were doing when it broke:

       fetchDeadlines().then(setDeadlines).catch(onFail('Deadlines', () => setDeadlines([])));

   The fallback still runs, so the interface does not crash into a blank page. The difference
   is that the failure is recorded, shown to the person, and named: they are told which piece
   of data is missing and why, rather than being shown an empty list and left to assume.

   WHY A STORE RATHER THAN A THROW.

   These are background loads on screens that are still usable without them. Throwing takes
   out the whole route, which is a worse outcome than a degraded panel. So the error is
   collected and surfaced alongside the working parts of the page.
   ===================================================================================== */

export interface AppError {
  id: number;
  /** What was being loaded, in the person's language: 'Deadlines', not 'fetchDeadlines'. */
  what: string;
  detail: string;
  at: number;
}

let seq = 0;
let errors: AppError[] = [];
const listeners = new Set<() => void>();

/* useSyncExternalStore compares snapshots by reference, so this must be the SAME array until
   something actually changes. Returning a fresh array each call is an infinite render loop,
   which is the bug that blanked the centre page earlier in this project. */
function emit() { listeners.forEach((l) => l()); }

export function subscribeErrors(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function errorSnapshot(): AppError[] { return errors; }

export function pushError(what: string, detail: string): void {
  // Collapse repeats. A failing query inside a polling effect would otherwise stack up
  // dozens of identical notices and bury everything else.
  if (errors.some((e) => e.what === what && e.detail === detail)) return;
  errors = [...errors, { id: ++seq, what, detail, at: Date.now() }];
  emit();
}

export function dismissError(id: number): void {
  errors = errors.filter((e) => e.id !== id);
  emit();
}

export function clearErrors(): void {
  if (!errors.length) return;
  errors = [];
  emit();
}

/**
 * Catch handler that records the failure, tells the person, and still runs a fallback so the
 * rest of the screen keeps working.
 *
 * @param what     Human-readable name of the data, used in the message shown on screen.
 * @param fallback Optional state reset, e.g. () => setRows([]).
 */
export function onFail(what: string, fallback?: () => void) {
  return (e: unknown): void => {
    const detail = e instanceof Error ? e.message : String(e);
    // Kept: the console line carries the stack, which the on-screen notice deliberately does not.
    console.error(`[forum] ${what} failed to load:`, e);
    pushError(what, detail);
    fallback?.();
  };
}
